import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { EntityData } from "@/services/api/types";
import {
  ActivityData,
  ActivityDataInput,
  ActivityNotesPatch,
  ActivityUpdateData,
  NewActivity
} from "@/entities/activities/types";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import {
  useCreateActivityMutation,
  useUpdateActivityMutation
} from "@/entities/activities/hooks";
import { newActivityToActivityType } from "@/components/layouts/Activities/Activities";
import { getUTCDateTime, toActivityFormValues } from "@/utils/datetime";
import { getEnumTKey } from "@/utils/strings";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { Close } from "@dreamhub/designsystem/icons";
import { isNull } from "lodash";

interface UseActivityModalSubmissionParams {
  activityType: NewActivity;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  entityType: EntityType;
  entityData: EntityData | undefined;
  relatedCompanyData: EntityData | undefined;
  pageName: string;
  resetPage?: () => void;
  setIsOpen: (open: boolean) => void;
}

interface UseActivityModalSubmissionReturn {
  handleSubmit: (
    data: ActivityDataInput,
    customTransform?: (
      baseData: ActivitySubmissionPayload
    ) => ActivitySubmissionPayload
  ) => Promise<void>;
  isSubmitting: boolean;
}

export type ActivitySubmissionPayload = ActivityUpdateData;

type ActivityModalNotesUpdate = ActivityNotesPatch & {
  fromEmail?: {
    email?: string;
    name?: string;
  };
  meetingContent?: string;
};

export const mergeActivitySubmissionNotes = (
  baseData: ActivitySubmissionPayload,
  notesUpdate: ActivityModalNotesUpdate
): ActivitySubmissionPayload => {
  if (baseData.notesPatch !== undefined) {
    return {
      ...baseData,
      notesPatch: {
        ...baseData.notesPatch,
        ...notesUpdate
      }
    };
  }

  return {
    ...baseData,
    notes: {
      ...baseData.notes,
      ...notesUpdate,
      location: notesUpdate.location ?? undefined
    }
  };
};

export const transformNoteActivitySubmission = (
  baseData: ActivitySubmissionPayload,
  attachments?: SavedAttachmentInfo[],
  isCreate = false
): ActivitySubmissionPayload => {
  const transformed = mergeActivitySubmissionNotes(
    baseData,
    attachments !== undefined ? { attachments } : {}
  );
  return isCreate
    ? {
        ...transformed,
        peopleIds: []
      }
    : transformed;
};

export const useActivityModalSubmission = ({
  activityType,
  isEditMode,
  editActivityData,
  entityType,
  entityData,
  relatedCompanyData,
  pageName,
  resetPage,
  setIsOpen
}: UseActivityModalSubmissionParams): UseActivityModalSubmissionReturn => {
  const editActivityId = editActivityData?.id ?? null;

  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createActivity = useCreateActivityMutation(pageName);
  const updateActivity = useUpdateActivityMutation(pageName);

  const getActivityTypeMapping = (activityType: NewActivity) => {
    if (activityType in newActivityToActivityType) {
      return newActivityToActivityType[activityType];
    }

    // For activities that use numeric types directly (like NDA, Meeting)
    // this will be handled by the customTransform function
    return null;
  };

  const getBaseTransformData = (
    data: ActivityDataInput
  ): ActivitySubmissionPayload => {
    const formOnlyFields = [
      "date",
      "participants",
      "time",
      "subject",
      "summary",
      "location",
      "dealId",
      "leadId",
      "companyId",
      "notes",
      "id",
      "createdAt"
    ];

    // A date and time left as prefilled keep the stored moment instead of re-stamping it
    const originalDate = isEditMode ? editActivityData?.notes?.date : undefined;
    const prefilled = originalDate ? toActivityFormValues(originalDate) : null;
    const isUnchanged =
      !!prefilled &&
      data.date.split("T")[0] === prefilled.date &&
      (!data.time || data.time === prefilled.time);

    // If time is empty, use current local time so the selected date stays intact
    const dateValue = isUnchanged
      ? originalDate
      : data.time
        ? getUTCDateTime(data.date, data.time)
        : (() => {
            const [year, month, day] = data.date
              .split("T")[0]
              .split("-")
              .map(Number);
            const now = new Date();
            return new Date(
              year,
              month - 1,
              day,
              now.getHours(),
              now.getMinutes(),
              now.getSeconds(),
              now.getMilliseconds()
            ).toISOString();
          })();

    const notes = {
      date: dateValue as NonNullable<ActivityUpdateData["notes"]>["date"],
      ...(data?.subject !== undefined ? { subject: data.subject } : {}),
      ...(data?.location !== undefined ? { location: data.location } : {}),
      ...(data?.summary !== undefined
        ? { summary: { summary: data.summary } }
        : {}),
      ...(!isEditMode ? { internalId: "" } : {})
    };

    return {
      ...Object.fromEntries(
        Object.entries(data).filter(([key]) => !formOnlyFields.includes(key))
      ),
      ...(!isEditMode
        ? {
            dealId: entityType === EntityType.DEAL ? entityData?.id : null,
            leadId: entityType === EntityType.LEAD ? entityData?.id : null,
            // will be overridden by customTransform if needed
            type: getActivityTypeMapping(activityType) ?? undefined,
            ...(relatedCompanyData
              ? {
                  companyId: relatedCompanyData.id
                }
              : {})
          }
        : {}),
      ...(data?.participants !== undefined
        ? {
            peopleIds: data?.participants
          }
        : {}),
      ...(isEditMode ? { notesPatch: notes } : { notes })
    };
  };

  const handleSuccess = async () => {
    await resetPage?.();
    toast.success(
      `${t(
        getEnumTKey("activities", "newActivities", NewActivity, activityType)
      )} ${t("activities.activityModal.success", {
        action: isEditMode
          ? t("activities.activityModal.updated")
          : t("activities.activityModal.created")
      })}`,
      {
        duration: 5000
      }
    );
    setIsOpen(false);
  };

  const handleError = () => {
    toast.error(
      <ToastBody
        title={t("activities.activityModal.error", {
          type: t(
            getEnumTKey(
              "activities",
              "newActivities",
              NewActivity,
              activityType
            )
          )
        })}
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      {
        icon: null
      }
    );
  };

  const handleSubmit = async (
    data: ActivityDataInput,
    customTransform?: (
      baseData: ActivitySubmissionPayload
    ) => ActivitySubmissionPayload
  ) => {
    setIsSubmitting(true);

    try {
      let transformFormData: ActivitySubmissionPayload =
        getBaseTransformData(data);
      if (customTransform) {
        transformFormData = customTransform(transformFormData);
      }

      const shouldUpdate = isEditMode && !isNull(editActivityId);
      const baseParams = { entityId: entityData?.id || "" };

      if (shouldUpdate && editActivityId) {
        await updateActivity.mutateAsync(
          {
            pathParams: { ...baseParams, activityId: editActivityId },
            data: transformFormData
          },
          { onSuccess: handleSuccess, onError: handleError }
        );
      } else {
        await createActivity.mutateAsync(
          {
            pathParams: baseParams,
            data: transformFormData as ActivityData
          },
          { onSuccess: handleSuccess, onError: handleError }
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    isSubmitting
  };
};
